import React from 'react';
import { Box, Button, Collapse, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import {
  AccessTime,
  Build,
  EventAvailable,
  Facebook,
  Instagram,
  Payments,
  Phone,
  Public as PublicIcon,
  SupportAgent,
  TaskAlt,
  WhatsApp,
  Email,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  FeatureCard,
  FooterContactRow,
  FooterEyebrow,
  FooterSocialButton,
  PublicTemplateProps,
  SectionHeading,
  StepCard,
} from './sharedPieces';
import { LocationMapView } from './LocationMapView';

// Full marketing page: dark hero, feature grid, fleet, how-it-works,
// about/contact, closing CTA. Shares nothing structurally with MinimalTemplate -
// only the data/handlers passed in via props.
export const ClassicTemplate: React.FC<PublicTemplateProps> = ({
  tenantCompany,
  tenantLogoUrl,
  logoLoadError,
  onLogoError,
  tenantLocationSummary,
  tenantWhatsappHref,
  searchFilterCard,
  successAlert,
  renderResultBody,
  handleScrollToSearch,
  datesReady,
}) => {
  const { t } = useTranslation();

  const hasMap = Boolean(
    tenantCompany?.googleMapsApiKey && tenantCompany?.latitude != null && tenantCompany?.longitude != null
  );

  const heroBadgeText = t('search.hero.badge', '24/7 Service');

  const featureIcons = [Build, Payments, AccessTime, EventAvailable, TaskAlt, SupportAgent];
  const featureCopy = [
    { title: t('search.whyChoose.feature1Title', 'Well-Maintained Vehicles'), description: t('search.whyChoose.feature1Desc', 'A modern fleet, regularly serviced for maximum safety and comfort.') },
    { title: t('search.whyChoose.feature2Title', 'Transparent Pricing'), description: t('search.whyChoose.feature2Desc', 'No hidden fees - clear pricing from the moment you book.') },
    { title: t('search.whyChoose.feature3Title', '24/7 Service'), description: t('search.whyChoose.feature3Desc', 'Pickup and drop-off support, any time you need it.') },
    { title: t('search.whyChoose.feature4Title', 'Easy Booking'), description: t('search.whyChoose.feature4Desc', 'Reserve online in minutes, with no complicated paperwork.') },
    { title: t('search.whyChoose.feature5Title', 'Guaranteed Delivery'), description: t('search.whyChoose.feature5Desc', 'Your vehicle will be ready at the time and place you booked.') },
    { title: t('search.whyChoose.feature6Title', 'Dedicated Support'), description: t('search.whyChoose.feature6Desc', 'A professional team, always available to help.') },
  ];

  const howItWorksSteps = [
    { title: t('search.howItWorks.step1Title', 'Choose your dates'), description: t('search.howItWorks.step1Desc', 'Pick your rental dates and filter by the type of vehicle you need.') },
    { title: t('search.howItWorks.step2Title', 'Select your vehicle'), description: t('search.howItWorks.step2Desc', 'Browse available cars and send a booking request in a few clicks.') },
    { title: t('search.howItWorks.step3Title', 'Get confirmation'), description: t('search.howItWorks.step3Desc', 'Our team will contact you to confirm availability and details.') },
  ];

  return (
    <>
      {/* HERO + SEARCH */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          color: 'common.white',
          background: 'linear-gradient(145deg, #0b0f19 0%, #1b2436 55%, #0b0f19 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% -10%, rgba(212,175,55,0.18), transparent 45%), radial-gradient(circle at 85% 110%, rgba(255,255,255,0.08), transparent 40%)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 3, sm: 5 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              mb: { xs: 3, sm: 4 },
              gap: { xs: 2, md: 0 },
            }}
          >
            {/* Left column - logo pinned to the true left edge. Same width as the right spacer
                so the middle column below stays truly centered regardless of the logo's size. */}
            <Box sx={{ flex: { md: '1 1 0' }, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
              {tenantLogoUrl && !logoLoadError && (
                <Box
                  component="img"
                  src={tenantLogoUrl}
                  alt={tenantCompany?.companyName}
                  onError={onLogoError}
                  sx={{
                    display: 'block',
                    width: 'auto',
                    height: 'auto',
                    maxHeight: { xs: 54, sm: 80 },
                    maxWidth: { xs: 220, sm: 320 },
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.35))',
                  }}
                />
              )}
            </Box>

            {/* Middle column - always centered in the full hero width. */}
            <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', flex: { md: '0 0 auto' } }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 5,
                  bgcolor: 'rgba(212,175,55,0.15)',
                  border: '1px solid rgba(212,175,55,0.4)',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 1, color: '#d4af37' }}>
                  {heroBadgeText.toUpperCase()}
                </Typography>
              </Box>

              <Typography
                variant="h3"
                sx={{ fontWeight: 800, letterSpacing: 0.3, fontSize: { xs: '1.75rem', sm: '2.5rem' }, maxWidth: 640 }}
              >
                {tenantCompany?.heroTagline || tenantCompany?.companyName || t('search.hero.fallbackHeadline', 'Find the right car for your trip')}
              </Typography>

              <Box
                sx={{
                  width: 64,
                  height: 3,
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #d4af37, rgba(212,175,55,0))',
                }}
              />

              {tenantCompany?.heroTagline && tenantCompany?.companyName && (
                <Typography variant="subtitle1" sx={{ opacity: 0.75 }}>
                  {tenantCompany.companyName}
                </Typography>
              )}
            </Stack>

            {/* Right spacer - mirrors the left column's width so the middle column above is
                truly centered, not just centered-relative-to-the-logo. */}
            <Box sx={{ flex: { md: '1 1 0' }, display: { xs: 'none', md: 'block' } }} />
          </Box>

          {successAlert}
          {searchFilterCard}
        </Container>
      </Box>

      {/* FLEET - directly under the search bar so changing dates/filters doesn't require
          scrolling past marketing sections to see the results. */}
      <Box sx={{ py: { xs: 2.5, sm: 4 } }}>
        <Container maxWidth="lg">
          <Collapse in={!datesReady} timeout={400} unmountOnExit>
            <SectionHeading
              eyebrow={t('search.fleet.eyebrow', 'Our Fleet')}
              headline={t('search.fleet.headline', 'Choose the right car for your trip')}
              subtitle={t('search.fleet.subtitle', 'Browse our fleet and find the vehicle that matches your needs, style, and itinerary.')}
            />
          </Collapse>
          {renderResultBody()}
        </Container>
      </Box>

      {/* WHY CHOOSE US */}
      <Box sx={{ py: { xs: 4, sm: 6 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={t('search.whyChoose.eyebrow', 'Why Choose Us')}
            headline={t('search.whyChoose.headline', 'A simple, convenient service for your trip')}
            subtitle={t('search.whyChoose.subtitle', 'From booking to pickup, our team assists you every step of the way.')}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {featureCopy.map((feature, index) => {
              const FeatureIcon = featureIcons[index];
              return (
                <FeatureCard
                  key={feature.title}
                  icon={<FeatureIcon fontSize="small" />}
                  title={feature.title}
                  description={feature.description}
                />
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* HOW IT WORKS */}
      <Box
        sx={{
          py: { xs: 6, sm: 9 },
          color: 'common.white',
          background: 'linear-gradient(145deg, #0b0f19 0%, #1b2436 55%, #0b0f19 100%)',
        }}
      >
        <Container maxWidth="lg">
          <SectionHeading
            light
            eyebrow={t('search.howItWorks.eyebrow', 'How It Works')}
            headline={t('search.howItWorks.headline', 'Book your car in 3 simple steps')}
            subtitle={t('search.howItWorks.subtitle', 'A simple process to choose and reserve your rental car.')}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 4,
            }}
          >
            {howItWorksSteps.map((step, index) => (
              <StepCard
                key={step.title}
                index={index + 1}
                title={step.title}
                description={step.description}
                light
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* ABOUT / CONTACT */}
      {tenantCompany && (
        <Box sx={{ py: { xs: 4, sm: 6 } }}>
          <Container maxWidth="lg">
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
              }}
            >
              {tenantCompany.description && (
                <Box sx={{ mb: 4 }}>
                  <FooterEyebrow>{t('search.aboutUs', 'About Us')}</FooterEyebrow>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line', lineHeight: 1.7, maxWidth: 760 }}
                  >
                    {tenantCompany.description}
                  </Typography>
                </Box>
              )}

              <Grid container spacing={{ xs: 4, md: 6 }}>
                <Grid item xs={12} md={hasMap ? 6 : 12}>
                  <FooterEyebrow>{t('search.contactUs', 'Contact Us')}</FooterEyebrow>
                  <Stack spacing={1.75}>
                    {tenantCompany.phoneNumber && (
                      <FooterContactRow icon={<Phone fontSize="small" />}>
                        <Typography variant="body2">{tenantCompany.phoneNumber}</Typography>
                      </FooterContactRow>
                    )}
                    {tenantCompany.email && (
                      <FooterContactRow icon={<Email fontSize="small" />}>
                        <Typography variant="body2">{tenantCompany.email}</Typography>
                      </FooterContactRow>
                    )}
                    {tenantCompany.website && (
                      <FooterContactRow icon={<PublicIcon fontSize="small" />}>
                        <Typography
                          component="a"
                          href={tenantCompany.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {tenantCompany.website}
                        </Typography>
                      </FooterContactRow>
                    )}
                    {(tenantLocationSummary ||
                      tenantCompany.operatingHoursStart ||
                      tenantCompany.operatingDays) && (
                      <FooterContactRow icon={<AccessTime fontSize="small" />}>
                        <Stack spacing={0.25}>
                          {tenantLocationSummary && (
                            <Typography variant="body2">{tenantLocationSummary}</Typography>
                          )}
                          {(tenantCompany.operatingHoursStart || tenantCompany.operatingDays) && (
                            <Typography variant="body2" color="text.secondary">
                              {tenantCompany.operatingDays}
                              {tenantCompany.operatingHoursStart && tenantCompany.operatingHoursEnd
                                ? ` · ${tenantCompany.operatingHoursStart}-${tenantCompany.operatingHoursEnd}`
                                : ''}
                            </Typography>
                          )}
                        </Stack>
                      </FooterContactRow>
                    )}
                    {(tenantCompany.instagramUrl || tenantCompany.facebookUrl || tenantWhatsappHref) && (
                      <Stack direction="row" spacing={1.25} sx={{ pt: 1 }}>
                        {tenantWhatsappHref && (
                          <FooterSocialButton href={tenantWhatsappHref} label="WhatsApp" color="#25D366">
                            <WhatsApp fontSize="small" />
                          </FooterSocialButton>
                        )}
                        {tenantCompany.instagramUrl && (
                          <FooterSocialButton href={tenantCompany.instagramUrl} label="Instagram" color="#E1306C">
                            <Instagram fontSize="small" />
                          </FooterSocialButton>
                        )}
                        {tenantCompany.facebookUrl && (
                          <FooterSocialButton href={tenantCompany.facebookUrl} label="Facebook" color="#1877F2">
                            <Facebook fontSize="small" />
                          </FooterSocialButton>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Grid>
                {hasMap && (
                  <Grid item xs={12} md={6}>
                    <LocationMapView
                      apiKey={tenantCompany.googleMapsApiKey}
                      latitude={tenantCompany.latitude}
                      longitude={tenantCompany.longitude}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Container>
        </Box>
      )}

      {/* FINAL CTA */}
      <Box
        sx={{
          py: { xs: 6, sm: 8 },
          textAlign: 'center',
          color: 'common.white',
          background: 'linear-gradient(145deg, #0b0f19 0%, #1b2436 55%, #0b0f19 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Stack spacing={2.5} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {t('search.finalCta.headline', 'Interested in renting a car?')}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.75 }}>
              {t('search.finalCta.subtitle', "Don't hesitate to contact us or book right away.")}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {tenantWhatsappHref && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<WhatsApp />}
                  component="a"
                  href={tenantWhatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ borderColor: 'rgba(255,255,255,0.4)' }}
                >
                  WhatsApp
                </Button>
              )}
              <Button
                variant="contained"
                sx={{ bgcolor: '#d4af37', color: '#0b0f19', '&:hover': { bgcolor: '#c39d2e' } }}
                onClick={handleScrollToSearch}
              >
                {t('search.finalCta.bookNow', 'Book Now')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
};
